import React, { useState, useEffect } from 'react';
import {
  View,
  StyleSheet,
  Platform,
  Animated,
  ViewStyle,
  Dimensions,
  Linking,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import Text from '../common/Text';
import Button from '../common/Button';
import { useTheme } from '../../hooks/useTheme';
import { API_URL } from '../../constants/config';
import { getToken } from '../../utils/storage';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { recheckConsentAndInitialize } from '../../utils/analytics';
import { createSessionId } from '../../utils/sessionId';

const MOBILE_BREAKPOINT = 600;

const CookieBanner: React.FC = () => {
  const { colors } = useTheme();
  const navigation = useNavigation();
  const [visible, setVisible] = useState(false);
  const [slideAnim] = useState(new Animated.Value(300));
  const [isNarrow, setIsNarrow] = useState(
    () => Dimensions.get('window').width < MOBILE_BREAKPOINT
  );

  useEffect(() => {
    const sub = Dimensions.addEventListener('change', ({ window }) => {
      setIsNarrow(window.width < MOBILE_BREAKPOINT);
    });
    return () => sub?.remove();
  }, []);

  useEffect(() => {
    void (async () => {
      try {
        const consent = await AsyncStorage.getItem('cookie_consent');
        if (!consent) {
          setVisible(true);
          Animated.spring(slideAnim, {
            toValue: 0,
            useNativeDriver: true,
          }).start();
        }
      } catch {
        // Consent check failed silently
      }
    })();
  }, [slideAnim]);

  const handleAccept = async () => {
    try {
      const token = await getToken('auth_token');
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      // Generate session ID if not exists
      let sessionId = await AsyncStorage.getItem('session_id');
      if (!sessionId) {
        sessionId = createSessionId();
        await AsyncStorage.setItem('session_id', sessionId);
      }

      await fetch(`${API_URL}/cookie-consent/`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          consent_given: true,
          consent_type: 'all',
          session_id: sessionId,
        }),
      });

      await AsyncStorage.setItem('cookie_consent', 'true');

      // Initialize Google Analytics after consent is given
      await recheckConsentAndInitialize();

      hideBanner();
    } catch {
      // Still hide the banner even if API call fails
      await AsyncStorage.setItem('cookie_consent', 'true');

      // Try to initialize GA even if API call failed
      await recheckConsentAndInitialize();

      hideBanner();
    }
  };

  const handleDecline = async () => {
    try {
      const token = await getToken('auth_token');
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      let sessionId = await AsyncStorage.getItem('session_id');
      if (!sessionId) {
        sessionId = createSessionId();
        await AsyncStorage.setItem('session_id', sessionId);
      }

      await fetch(`${API_URL}/cookie-consent/`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          consent_given: false,
          consent_type: 'essential',
          session_id: sessionId,
        }),
      });

      await AsyncStorage.setItem('cookie_consent', 'declined');
      hideBanner();
    } catch {
      await AsyncStorage.setItem('cookie_consent', 'declined');
      hideBanner();
    }
  };

  const hideBanner = () => {
    const fallbackId = setTimeout(() => setVisible(false), 400);
    Animated.timing(slideAnim, {
      toValue: 300,
      duration: 300,
      useNativeDriver: true,
    }).start(({ finished }) => {
      clearTimeout(fallbackId);
      if (finished) setVisible(false);
    });
  };

  if (!visible) {
    return null;
  }

  const handlePrivacyPress = () => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const base = window.location?.origin || '';
      Linking.openURL(`${base}/privacy`);
    } else {
      navigation.navigate('PrivacyPolicy');
    }
  };

  return (
    <Animated.View
      style={[
        { pointerEvents: 'auto' },
        styles.container,
        {
          backgroundColor: colors.card,
          borderTopColor: colors.border,
          transform: [{ translateY: slideAnim }],
        },
      ]}
      accessibilityRole="alert"
      accessibilityLabel="Cookie consent banner"
    >
      <View
        style={[
          styles.content,
          isNarrow && styles.contentNarrow,
          Platform.OS === 'web' && styles.contentWeb,
        ]}
      >
        <Text style={[styles.text, { color: colors.text }]}>
          We use cookies to enhance your experience, analyze site usage, and
          assist in our marketing efforts.{' '}
          <Text
            onPress={handlePrivacyPress}
            style={[
              styles.privacyLink,
              { color: colors.primary },
              Platform.OS === 'web' && styles.privacyLinkWeb,
            ]}
            accessibilityRole="link"
            accessibilityLabel="View Privacy Policy"
          >
            Learn more
          </Text>{' '}
          By clicking &quot;Accept&quot;, you consent to our use of cookies.
        </Text>
        <View style={[styles.buttons, isNarrow && styles.buttonsNarrow]}>
          <Button
            title="Decline"
            onPress={handleDecline}
            variant="outline"
            style={[styles.button, styles.declineButton] as ViewStyle}
            textStyle={styles.declineButtonText}
            hoverColor={colors.authBackground}
          />
          <Button
            title="Accept"
            onPress={handleAccept}
            style={[styles.button, styles.acceptButton] as ViewStyle}
          />
        </View>
      </View>
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    borderTopWidth: 1,
    ...(Platform.OS === 'web'
      ? {
          boxShadow: '0px -4px 20px rgba(0, 0, 0, 0.1)',
        }
      : {
          shadowColor: '#000',
          shadowOffset: { width: 0, height: -4 },
          shadowOpacity: 0.1,
          shadowRadius: 12,
          elevation: 8,
        }),
    zIndex: 10000,
  },
  content: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 20,
    gap: 16,
  },
  contentNarrow: {
    flexDirection: 'column',
    alignItems: 'stretch',
    paddingVertical: 16,
    paddingHorizontal: 16,
  },
  contentWeb: {
    // @ts-expect-error - touchAction is web-only, not in React Native ViewStyle
    touchAction: 'manipulation',
  },
  text: {
    flex: 1,
    fontSize: 14,
    lineHeight: 20,
    ...(Platform.OS === 'web' ? { minWidth: 0 } : {}),
  },
  buttons: {
    flexDirection: 'row',
    flexShrink: 0,
    gap: 12,
    alignItems: 'center',
  },
  buttonsNarrow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: 8,
    gap: 12,
    alignSelf: 'flex-end',
  },
  button: {
    minWidth: 100,
    minHeight: 44,
    ...(Platform.OS === 'web' ? { cursor: 'pointer' } : {}),
  },
  declineButton: {
    borderWidth: 1,
  },
  declineButtonText: {
    fontSize: 14,
  },
  acceptButton: {
    minWidth: 100,
  },
  privacyLink: {
    textDecorationLine: 'underline',
    fontWeight: '600',
    paddingVertical: 4,
    paddingHorizontal: 2,
  },
  privacyLinkWeb: {
    // @ts-expect-error - cursor is web-only style for larger click target
    cursor: 'pointer',
  },
});

export default CookieBanner;
